import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Stack, FormHelperText } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import permissionConfig from '@config/permissionConfig';
import {
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  createSearchState,
  useLocalization,
  useStore,
  useUserAccess,
  FormSearchResultAdvanced,
  Label,
  SubSection,
} from '@ui-framework/index';
import {
  IconLibrary,
  StackablePageButtons,
  FormFields,
  ExportButton,
  getDefaultValueByControl,
} from '@product-framework/index';
import { formatUSPhoneNumber } from '@product-framework/components/FormFields/PhoneNumber';
import { useServiceModule } from '@product-framework/components/ServiceModuleProvider';
import { useServiceProviderScope } from '@product-framework/pages/ServiceProvider/scope';
import {
  InsightContactProvider,
  useInsightContact,
} from './components/InsightContactContext';
import InsightContactDeleteDialog from './components/InsightContactDeleteDialog';
import InsightContactSmsDisclosure from './components/InsightContactSmsDisclosure';
import InsightContactList from './pages/list';
import InsightContactCreate from './pages/create';
import InsightContactEdit from './pages/edit';
import validationSchema from './pages/list/validations';
import { searchSchema } from './schemas';

const STORE_NAME = 'InsightContactListStore';

function InsightContactInner({ onChange = () => undefined }) {
  const { selectedRows } = useInsightContact();
  const { formatMessage } = useLocalization();
  const tableRef = useRef(null);
  const { moduleSettings } = useServiceModule();
  const { isLocationScope, skillsConfig } = useServiceProviderScope();
  const userAccess = useUserAccess();

  const store = useStore();

  const setupApi = skillsConfig?.api?.setup ?? {};
  const setupMessages = skillsConfig?.ui?.messages?.setup ?? {};

  const refreshTable = useCallback(() => {
    tableRef.current?.refresh?.();
    onChange();
  }, [onChange]);

  const canWrite = permissionConfig({
    userAccess,
    permissionName: 'CAN_UPDATE_SP_INSIGHT_CONTACTS',
  });

  const commonParams = useMemo(
    () => ({
      phonesUrl: setupApi.phonesUrl,
      bulkDeletePhonesUrl: setupApi.deleteSelectedPhones,
      exportPhonesUrl: setupApi.exportPhones,
      phonesSaveSuccess: setupMessages.phonesSaveSuccess,
      phonesSaveWarning: setupMessages.phonesSaveWarning,
      insightPurchased: moduleSettings?.insightPurchased ?? false,
      refreshTable,
      canWrite,
    }),
    [
      setupApi.phonesUrl,
      setupApi.deleteSelectedPhones,
      setupApi.exportPhones,
      setupMessages.phonesSaveSuccess,
      setupMessages.phonesSaveWarning,
      moduleSettings?.insightPurchased,
      refreshTable,
      canWrite,
    ],
  );

  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(0);

  const [action, setAction] = useState(null);
  const [actionContact, setActionContact] = useState(null);

  const onClose = useCallback(() => {
    setAction(null);
    setActionContact(null);
  }, []);

  useEffect(() => {
    onClose();
  }, [setupApi.phonesUrl, onClose]);

  const onCreate = useCallback(() => {
    setAction('create');
    setActionContact(null);
  }, []);

  const onEdit = useCallback(row => {
    setAction('edit');
    setActionContact(row);
  }, []);

  const onDelete = useCallback(row => {
    setAction('delete');
    setActionContact(row);
  }, []);

  const onDeleteSelected = useCallback(() => {
    setAction('bulkDelete');
    setActionContact(null);
  }, []);

  const onRowCountChange = useCallback(rowCount => {
    setResultsFound(rowCount);
  }, []);

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE',
        defaultMessage: 'Create',
      }),
      Icon: IconLibrary.Add,
      permissionName: 'CAN_UPDATE_SP_INSIGHT_CONTACTS',
      variant: 'outlined',
      onClick: onCreate,
    },
    {
      label: formatMessage({
        id: 'DELETE',
        defaultMessage: 'Delete',
      }),
      Icon: IconLibrary.Delete,
      permissionName: 'CAN_UPDATE_SP_INSIGHT_CONTACTS',
      variant: 'outlined',
      color: 'error',
      onClick: onDeleteSelected,
      disabled: selectedRows.length === 0,
    },
    {
      label: showSearch
        ? formatMessage({
            id: 'HIDE_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Hide Search',
          })
        : formatMessage({
            id: 'INSIGHT_CONTACT_TOOLBAR_SEARCH',
            defaultMessage: 'Search',
          }),
      Icon: showSearch ? IconLibrary.HideSearch : IconLibrary.Search,
      variant: 'contained',
      onClick: () => setShowSearch(prev => !prev),
    },
  ];

  const preProcessSearchData = data => {
    return Object.fromEntries(
      Object.entries(data ?? {}).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const searchState = createSearchState(
      preProcessSearchData(data),
      searchSchema,
    );
    store.domain.list.set(STORE_NAME, searchState);
  };

  const searchProps = {
    customComponents: FormFields,
    storeName: STORE_NAME,
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => store.domain.list.reset(STORE_NAME),
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => setShowSearch(false),
    getValueByControl: getDefaultValueByControl,
  };

  const searchParamsProps = {
    storeName: STORE_NAME,
    schema: searchSchema,
    resultsFound,
    valueFormatter: ({ name, value }) =>
      name === 'phoneNumber' ? formatUSPhoneNumber(value) : value,
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Label style={{ display: 'block', marginBottom: '5px' }}>
        {formatMessage({
          id: 'INSIGHT_CONTACT_SECTION_TITLE',
          defaultMessage: 'Customer Communication',
        })}
      </Label>

      <InsightContactSmsDisclosure />

      <SubSection>
        <Grid2 container spacing={2}>
          <Grid2 xs={12}>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
              flexWrap="wrap"
              useFlexGap>
              <StackablePageButtons
                backButton={false}
                buttons={stackablePageButtons}
              />
              <ExportButton
                table={tableRef}
                permissionName="CAN_UPDATE_SP_INSIGHT_CONTACTS"
                exportUrl={commonParams.exportPhonesUrl}
                filePrefix="insight-contact"
              />
            </Stack>
          </Grid2>

          {showSearch && (
            <Grid2 xs={12}>
              <SubSection>
                <Grid2 container spacing={2}>
                  <Grid2 xs={12}>
                    <FormSearchResultAdvanced {...searchParamsProps} />
                  </Grid2>
                  <Grid2 xs={12}>
                    <CRUDComponent
                      type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
                      searchProps={searchProps}
                    />
                  </Grid2>
                </Grid2>
              </SubSection>
            </Grid2>
          )}

          <Grid2 xs={12}>
            <SubSection>
              <InsightContactList
                ref={tableRef}
                onEdit={onEdit}
                onDelete={onDelete}
                onRowCountChange={onRowCountChange}
                commonParams={commonParams}
              />
            </SubSection>
          </Grid2>

          {isLocationScope && resultsFound > 0 && (
            <Grid2 xs={12}>
              <FormHelperText sx={{ typography: 'body2' }}>
                When no default is set, the Service Provider level default will
                be used.
              </FormHelperText>
            </Grid2>
          )}
        </Grid2>

        {canWrite && (
          <>
            <InsightContactCreate
              open={action === 'create'}
              onClose={onClose}
              commonParams={commonParams}
            />
            <InsightContactEdit
              open={action === 'edit'}
              editContact={actionContact}
              onClose={onClose}
              commonParams={commonParams}
            />
            <InsightContactDeleteDialog
              action={action}
              deleteContact={actionContact}
              onClose={onClose}
              commonParams={commonParams}
            />
          </>
        )}
      </SubSection>
    </Box>
  );
}

function InsightContact({ onChange = () => undefined }) {
  const { skillsConfig } = useServiceProviderScope();
  const phonesUrl = skillsConfig?.api?.setup?.phonesUrl ?? '';

  return (
    <InsightContactProvider scopeKey={phonesUrl}>
      <InsightContactInner onChange={onChange} />
    </InsightContactProvider>
  );
}

export default observer(InsightContact);
